import { useEffect, useRef, type RefObject } from 'react';
import { RecitationSource, positionAtTime } from '../../shared/quran/recitation';
import { QuranWordsCache, locForTimingPosition, verseKeyOf, wordAudioUrl, type LaidOutWord } from '../../shared/quran/wordAudio';
import type { ReaderHost } from './readerHost';

/* Quran.com's word lists and timings are the same for every reader on the page: cache them for the session. */
const quranWords = new QuranWordsCache();
const recitation = new RecitationSource();

/**
 * Per-word audio for the reader: tap a word to hear it (one player, the
 * previous word always cleared, a later tap always wins), and while the
 * reciter plays, light up each word in time using Quran.com's timings for
 * the chosen reciter, mapped back to our words by their letters. Highlights
 * are class toggles on the rendered word nodes (`.playing`, `.reciting`),
 * which React leaves alone because their markup never changes.
 */
export function useWordAudio(host: ReaderHost, contentRef: RefObject<HTMLElement | null>, laidOut: (verseKey: string) => LaidOutWord[]) {
  const tapToken = useRef(0);
  const following = useRef<string | null>(null);
  const frame = useRef(0);

  const clear = (cls: 'playing' | 'reciting') => {
    contentRef.current?.querySelectorAll('.' + cls).forEach((el) => el.classList.remove(cls));
  };

  const playWord = async (loc: string, token: HTMLElement) => {
    const mine = ++tapToken.current;
    clear('playing');
    const audio = host.audio().tapElement();
    audio.pause();
    token.classList.add('playing');
    const url = await wordAudioUrl(loc, laidOut(verseKeyOf(loc)), quranWords);
    if (mine !== tapToken.current) return;   /* a later tap took over */
    const done = () => { if (mine === tapToken.current) token.classList.remove('playing'); };
    if (!url) { done(); return; }
    audio.src = url; audio.currentTime = 0;
    audio.onended = done; audio.onerror = done;
    const playing = audio.play();
    if (playing && typeof playing.catch === 'function') playing.catch(done);
  };

  /** Follow the reciter through `verseKey`, or stop following (null). */
  const follow = (verseKey: string | null) => {
    cancelAnimationFrame(frame.current);
    clear('reciting');
    following.current = verseKey;
    if (!verseKey) return;
    const audio = host.audio();
    const verseWords = laidOut(verseKey);
    Promise.all([recitation.wordTimings(audio.reciterId(), verseKey), quranWords.words(verseKey)]).then(([segments, words]) => {
      if (following.current !== verseKey || !segments.length) return;
      let lastPosition: number | null = null;
      const tick = () => {
        if (following.current !== verseKey) return;
        const position = positionAtTime(segments, (audio.element.currentTime || 0) * 1000);
        if (position !== lastPosition) {
          lastPosition = position;
          clear('reciting');
          const loc = position == null ? null : locForTimingPosition(position, verseWords, words);
          if (loc) contentRef.current?.querySelectorAll('[data-loc="' + loc + '"]').forEach((el) => el.classList.add('reciting'));
        }
        frame.current = requestAnimationFrame(tick);
      };
      tick();
    });
  };

  useEffect(() => () => {
    cancelAnimationFrame(frame.current);
    following.current = null;
    tapToken.current++;
  }, []);

  return { playWord, follow };
}
